import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { Plus, Users, Mail, Trash2, UserCheck, UserX, Copy, ExternalLink, MessageSquare, Edit } from 'lucide-react';
import axios from 'axios';
import { UserDetailsModal } from './UserDetailsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  
  // Invite form state
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteTeam, setInviteTeam] = useState('MG&BK Fodbold');
  const [inviteCustomMessage, setInviteCustomMessage] = useState('');
  const [inviteError, setInviteError] = useState('');
  
  // Custom message state
  const [customMessages, setCustomMessages] = useState({});
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [currentMessageType, setCurrentMessageType] = useState('');
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  
  // User details modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserDetailsOpen, setIsUserDetailsOpen] = useState(false);
  
  // Delete confirmation state
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  // Logo settings state
  const [logoSettings, setLogoSettings] = useState({
    gymnastik_size: 60,
    mgbk_size: 64,
    rub_size: 64
  });
  const [logoSaving, setLogoSaving] = useState(false);
  
  const TEAMS = ['Rock Under Broen', 'MG&BK Gymnastik', 'MG&BK Fodbold'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, invitationsRes, auditRes, messagesRes, logoRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/invitations`),
        axios.get(`${API}/admin/audit-logs?limit=50`),
        axios.get(`${API}/admin/messages`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/logo-settings`).catch(() => ({ data: { gymnastik_size: 60, mgbk_size: 64, rub_size: 64 } }))
      ]);
      
      setUsers(usersRes.data);
      setInvitations(invitationsRes.data);
      setAuditLogs(auditRes.data);
      
      // Process custom messages into a lookup object
      const messageMap = {};
      messagesRes.data.forEach(msg => {
        messageMap[msg.message_type] = msg;
      });
      setCustomMessages(messageMap);
      
      // Set logo settings
      setLogoSettings(logoRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Kunne ikke indlæse admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setInviteError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/invite`, { 
        email: inviteEmail,
        team: inviteTeam,
        custom_message: inviteCustomMessage || null
      });
      
      // Show invitation link in a modal or copy to clipboard
      const inviteLink = response.data.invite_link;
      
      // Try to copy to clipboard, but don't let it fail the whole process
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(inviteLink);
          toast.success(`📧 Invitation email sendt til ${inviteEmail}! Link også kopieret til udklipsholder.`);
        } else {
          // Fallback for older browsers
          toast.success(`📧 Invitation email sendt til ${inviteEmail}!`);
        }
      } catch (clipboardError) {
        // If clipboard fails, still show success but mention email was sent
        toast.success(`📧 Invitation email sendt til ${inviteEmail}! Se 'Kopier Link' knappen for backup.`);
      }
      
      setInviteEmail('');
      setInviteTeam('MG&BK Fodbold');
      setInviteCustomMessage('');
      setIsInviteDialogOpen(false);
      fetchData(); // Refresh data
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Kunne ikke oprette invitation';
      setInviteError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await axios.delete(`${API}/admin/invitations/${invitationId}`);
      toast.success('Invitation annulleret');
      fetchData();
    } catch (error) {
      toast.error('Kunne ikke annullere invitation');
    }
  };

  const handleClearUsedInvitations = async () => {
    if (!window.confirm('Er du sikker på at du vil slette alle brugte invitationer?')) {
      return;
    }

    try {
      setLoading(true);
      
      // Call backend endpoint to delete all used invitations
      const response = await axios.delete(`${API}/admin/invitations/clear-used`);
      
      toast.success(response.data.message || 'Brugte invitationer er slettet');
      fetchData(); // Refresh the list
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Kunne ikke rydde brugte invitationer';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const endpoint = currentStatus ? 'deactivate' : 'activate';
      await axios.patch(`${API}/admin/users/${userId}/${endpoint}`);
      toast.success(`Bruger ${currentStatus ? 'deaktiveret' : 'aktiveret'}`);
      fetchData();
    } catch (error) {
      toast.error('Kunne ikke ændre bruger status');
    }
  };

  const formatDateTime = (dateString) => {
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: da });
  };

  const copyInviteLink = async (inviteToken) => {
    const inviteLink = `${BACKEND_URL}/accept-invite?token=${inviteToken}`;
    
    // Method 1: Try modern clipboard API
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success('Link kopieret til udklipsholder!');
      return;
    } catch (clipboardError) {
      console.log('Modern clipboard failed, trying fallback');
    }
    
    // Method 2: Fallback using textarea + execCommand
    try {
      const textArea = document.createElement('textarea');
      textArea.value = inviteLink;
      
      // Make sure the textarea is visible but off-screen
      textArea.style.position = 'absolute';
      textArea.style.left = '-9999px';
      textArea.style.top = '0px';
      textArea.setAttribute('readonly', '');
      
      document.body.appendChild(textArea);
      
      // For mobile devices
      if (navigator.userAgent.match(/ipad|android|iphone/i)) {
        textArea.contentEditable = true;
        textArea.readOnly = true;
        const range = document.createRange();
        range.selectNodeContents(textArea);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        textArea.setSelectionRange(0, 999999);
      } else {
        textArea.select();
      }
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success('Link kopieret til udklipsholder!');
      } else {
        throw new Error('execCommand failed');
      }
    } catch (fallbackError) {
      // Last resort: Show error message
      toast.error('Kunne ikke kopiere automatisk. Prøv at kopiere linket manuelt fra invitation listen.');
    }
  };

  const openUserDetails = (user) => {
    setSelectedUser(user);
    setIsUserDetailsOpen(true);
  };

  const openMessageDialog = (messageType) => {
    setCurrentMessageType(messageType);
    const existingMessage = customMessages[messageType];
    if (existingMessage) {
      setMessageSubject(existingMessage.subject || '');
      setMessageContent(existingMessage.content || '');
    } else {
      setMessageSubject('');
      setMessageContent('');
    }
    setIsMessageDialogOpen(true);
  };

  const handleSaveCustomMessage = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/messages`, {
        message_type: currentMessageType,
        subject: messageSubject,
        content: messageContent
      });
      
      toast.success(`${currentMessageType === 'invitation' ? 'Invitation' : 'Booking bekræftelse'} besked gemt`);
      setIsMessageDialogOpen(false);
      fetchData(); // Refresh to get updated messages
    } catch (error) {
      toast.error('Kunne ikke gemme besked');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomMessage = async (messageType) => {
    try {
      await axios.delete(`${API}/admin/messages/${messageType}`);
      toast.success('Brugerdefineret besked slettet - bruger nu standard besked');
      fetchData();
    } catch (error) {
      toast.error('Kunne ikke slette besked');
    }
  };

  const handleUpdateUserTeam = async (userId, newTeam) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/team`, { team: newTeam });
      toast.success(`Bruger team opdateret til ${newTeam}`);
      fetchData();
    } catch (error) {
      toast.error('Kunne ikke opdatere bruger team');
    }
  };

  const handleDeleteUser = (userId, userEmail, userName) => {
    // Show confirmation dialog
    setUserToDelete({ id: userId, email: userEmail, name: userName });
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setLoading(true);
      await axios.delete(`${API}/admin/users/${userToDelete.id}`);
      toast.success(`Bruger ${userToDelete.email} er blevet slettet`);
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Kunne ikke slette bruger';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLogoSettings = async () => {
    try {
      setLogoSaving(true);
      await axios.post(`${API}/admin/logo-settings`, logoSettings);
      toast.success('Logo indstillinger er gemt!');
      // Reload the page to apply new logo sizes
      window.location.reload();
    } catch (error) {
      toast.error('Kunne ikke gemme logo indstillinger');
    } finally {
      setLogoSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Admin Panel</h2>
          <p className="text-indigo-600 font-medium">Administrer brugere og invitationer</p>
        </div>
        
        <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white shadow-xl shadow-emerald-500/25 hover:shadow-2xl hover:shadow-emerald-500/30 transition-all duration-300 border-0">
              <Plus className="w-4 h-4 mr-2" />
              Inviter Bruger
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Inviter Ny Bruger</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleInviteUser} className="space-y-4">
              {inviteError && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-800">{inviteError}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="inviteEmail">Email adresse</Label>
                <Input
                  id="inviteEmail"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="bruger@email.dk"
                  required
                  disabled={loading}
                />
                <p className="text-xs text-slate-600">
                  Invitationen sendes automatisk til brugerens email adresse med alle nødvendige instruktioner.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="inviteTeam">Team</Label>
                <select
                  id="inviteTeam"
                  value={inviteTeam}
                  onChange={(e) => setInviteTeam(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                >
                  {TEAMS.map(team => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-600">
                  {inviteTeam === 'Rock Under Broen' ? 'Kun dette team kan booke "Rocken"' : 'Dette team kan booke alle lokaler undtagen "Rocken"'}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="inviteCustomMessage">Brugerdefineret besked (valgfri)</Label>
                <textarea
                  id="inviteCustomMessage"
                  value={inviteCustomMessage}
                  onChange={(e) => setInviteCustomMessage(e.target.value)}
                  placeholder="Skriv en personlig besked til den inviterede bruger..."
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={3}
                  disabled={loading}
                />
                <p className="text-xs text-slate-600">
                  Denne besked vil blive vist i invitationsemail sammen med standard tekst.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setIsInviteDialogOpen(false)}
                  className="flex-1"
                  disabled={loading}
                >
                  Annuller
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={loading}
                >
                  {loading ? 'Sender...' : 'Send Invitation'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tab Navigation */}
      <div className="border-b">
        <div className="flex space-x-8">
          <button
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('users')}
          >
            <Users className="w-4 h-4 inline-block mr-2" />
            Brugere ({users.length})
          </button>
          <button
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'invitations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('invitations')}
          >
            <Mail className="w-4 h-4 inline-block mr-2" />
            Invitationer ({invitations.length})
          </button>
          {activeTab === 'invitations' && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearUsedInvitations}
              disabled={loading}
              className="ml-auto self-center"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Ryd Brugte ({invitations.filter(inv => inv.used_at).length})
            </Button>
          )}
          <button
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'messages'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('messages')}
          >
            <MessageSquare className="w-4 h-4 inline-block mr-2" />
            Beskeder
          </button>
          <button
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'logos'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('logos')}
          >
            <Edit className="w-4 h-4 inline-block mr-2" />
            Logo Indstillinger
          </button>
          <button
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'audit'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('audit')}
          >
            Aktivitets Log
          </button>
        </div>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <Card>
          <CardHeader>
            <CardTitle>Brugere</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {users.map(user => (
                <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors" 
                     onClick={() => openUserDetails(user)}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold">{user.name || user.email}</h3>
                      <Badge variant={user.role === 'ADMIN' ? 'default' : 'secondary'}>
                        {user.role}
                      </Badge>
                      <Badge variant={user.is_active ? 'default' : 'destructive'} 
                             className={user.is_active ? 'bg-green-600 hover:bg-green-700' : ''}>
                        {user.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </div>
                    <div className="text-sm text-slate-600 mt-1 space-y-1">
                      <p>Email: {user.email}</p>
                      <div className="flex items-center gap-2">
                        <span>Team:</span>
                        <select
                          value={user.team || 'MG&BK Fodbold'}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleUpdateUserTeam(user.id, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {TEAMS.map(team => (
                            <option key={team} value={team}>{team}</option>
                          ))}
                        </select>
                      </div>
                      <p>Oprettet: {formatDateTime(user.created_at)}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant={user.is_active ? 'destructive' : 'default'}
                      size="sm"
                      onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                    >
                      {user.is_active ? (
                        <>
                          <UserX className="w-4 h-4 mr-2" />
                          Deaktiver
                        </>
                      ) : (
                        <>
                          <UserCheck className="w-4 h-4 mr-2" />
                          Aktiver
                        </>
                      )}
                    </Button>
                    
                    {/* Only show delete button for non-admin users */}
                    {user.role !== 'ADMIN' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteUser(user.id, user.email, user.name || user.email);
                        }}
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Slet
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <Card>
          <CardHeader>
            <CardTitle>Invitationer</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="mb-4 flex justify-end">
              <button
                onClick={handleClearUsedInvitations}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Ryd Brugte Invitationer
              </button>
            </div>
            <div className="space-y-4">
              {invitations.map(invitation => {
                const isExpired = new Date(invitation.expires_at) < new Date();
                const isUsed = invitation.used_at;
                
                return (
                  <div key={invitation.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold">{invitation.email}</h3>
                        <Badge variant={isUsed ? 'default' : isExpired ? 'destructive' : 'secondary'}>
                          {isUsed ? 'Brugt' : isExpired ? 'Udløbet' : 'Afventer'}
                        </Badge>
                      </div>
                      <div className="text-sm text-slate-600 mt-1 space-y-1">
                        <p>Oprettet: {formatDateTime(invitation.created_at)}</p>
                        <p>Udløber: {formatDateTime(invitation.expires_at)}</p>
                        {isUsed && <p>Brugt: {formatDateTime(invitation.used_at)}</p>}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      {!isUsed && !isExpired && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyInviteLink(invitation.token)}
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Kopier Link
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleCancelInvitation(invitation.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Annuller
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Audit Log Tab */}
      {activeTab === 'audit' && (
        <Card>
          <CardHeader>
            <CardTitle>Aktivitets Log</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {auditLogs.map(log => (
                <div key={log.id} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge>{log.action}</Badge>
                      <span className="font-medium">{log.resource_type}</span>
                    </div>
                    <span className="text-sm text-slate-600">
                      {formatDateTime(log.timestamp)}
                    </span>
                  </div>
                  
                  {log.details && (
                    <div className="mt-2 text-sm text-slate-600">
                      <pre className="whitespace-pre-wrap">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <Card>
          <CardHeader>
            <CardTitle>Brugerdefinerede Beskeder</CardTitle>
            <p className="text-sm text-slate-600">Tilpas beskeder der sendes til brugerne</p>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Invitation Message */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">Invitations Besked</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openMessageDialog('invitation')}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Rediger
                  </Button>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Besked der vises når nye brugere inviteres til systemet
                </p>
                {customMessages.invitation ? (
                  <div className="bg-slate-50 p-3 rounded border-l-4 border-blue-500">
                    <p className="font-medium text-sm">{customMessages.invitation.subject}</p>
                    <p className="text-sm mt-1">{customMessages.invitation.content}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteCustomMessage('invitation')}
                      className="mt-2 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Slet & Brug Standard
                    </Button>
                  </div>
                ) : (
                  <div className="bg-slate-100 p-3 rounded border-l-4 border-slate-400">
                    <p className="text-sm text-slate-600">Bruger standard invitation besked</p>
                  </div>
                )}
              </div>

              {/* Booking Confirmation Message */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">Booking Bekræftelse Besked</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openMessageDialog('booking_confirmation')}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Rediger
                  </Button>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Besked der vises når brugere får bekræftet deres bookinger
                </p>
                {customMessages.booking_confirmation ? (
                  <div className="bg-slate-50 p-3 rounded border-l-4 border-green-500">
                    <p className="font-medium text-sm">{customMessages.booking_confirmation.subject}</p>
                    <p className="text-sm mt-1">{customMessages.booking_confirmation.content}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteCustomMessage('booking_confirmation')}
                      className="mt-2 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Slet & Brug Standard
                    </Button>
                  </div>
                ) : (
                  <div className="bg-slate-100 p-3 rounded border-l-4 border-slate-400">
                    <p className="text-sm text-slate-600">Bruger standard booking bekræftelse besked</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Logo Settings Tab */}
      {activeTab === 'logos' && (
        <Card>
          <CardHeader>
            <CardTitle>Logo Indstillinger</CardTitle>
            <p className="text-sm text-slate-600">Juster størrelsen på logoerne i navigationen</p>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              {/* Gymnastik Logo */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Gymnastik Logo</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="20"
                    max="200"
                    value={logoSettings.gymnastik_size}
                    onChange={(e) => setLogoSettings({...logoSettings, gymnastik_size: parseInt(e.target.value) || 60})}
                    className="w-32"
                  />
                  <span className="text-sm text-slate-600">pixels (højde)</span>
                </div>
                <div className="text-xs text-slate-500">Anbefalet: 45-90 pixels</div>
              </div>

              {/* MG & BK Logo */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">MG & BK Logo</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="20"
                    max="200"
                    value={logoSettings.mgbk_size}
                    onChange={(e) => setLogoSettings({...logoSettings, mgbk_size: parseInt(e.target.value) || 64})}
                    className="w-32"
                  />
                  <span className="text-sm text-slate-600">pixels (højde)</span>
                </div>
                <div className="text-xs text-slate-500">Anbefalet: 48-80 pixels</div>
              </div>

              {/* Rock Under Broen Logo */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Rock Under Broen Logo</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="20"
                    max="200"
                    value={logoSettings.rub_size}
                    onChange={(e) => setLogoSettings({...logoSettings, rub_size: parseInt(e.target.value) || 64})}
                    className="w-32"
                  />
                  <span className="text-sm text-slate-600">pixels (højde)</span>
                </div>
                <div className="text-xs text-slate-500">Anbefalet: 48-80 pixels</div>
              </div>

              <div className="pt-4 border-t">
                <Button 
                  onClick={handleSaveLogoSettings}
                  disabled={logoSaving}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                >
                  {logoSaving ? 'Gemmer...' : 'Gem Logo Indstillinger'}
                </Button>
                <p className="text-xs text-slate-500 mt-2">Siden vil genindlæse for at anvende ændringer</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Custom Message Dialog */}
      <Dialog open={isMessageDialogOpen} onOpenChange={setIsMessageDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {currentMessageType === 'invitation' ? 'Rediger Invitations Besked' : 'Rediger Booking Bekræftelse Besked'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCustomMessage} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="messageSubject">Email Emne</Label>
              <Input
                id="messageSubject"
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
                placeholder={currentMessageType === 'invitation' ? 'Invitation til Mødelokale Booking System' : 'Booking Bekræftelse - [Titel]'}
                required
                disabled={loading}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="messageContent">Besked Indhold</Label>
              <textarea
                id="messageContent"
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder={
                  currentMessageType === 'invitation' 
                    ? 'Skriv din brugerdefinerede invitation besked her. Denne vil blive vist i invitationsemail.' 
                    : 'Skriv din brugerdefinerede booking bekræftelse besked her. Denne vil blive vist i booking emails.'
                }
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows={5}
                required
                disabled={loading}
              />
              <p className="text-xs text-slate-600">
                Beskeden vil blive vist sammen med standard booking information og logoen.
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => setIsMessageDialogOpen(false)}
                className="flex-1"
                disabled={loading}
              >
                Annuller
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? 'Gemmer...' : 'Gem Besked'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* User Details Modal */}
      <UserDetailsModal
        user={selectedUser}
        isOpen={isUserDetailsOpen}
        onClose={() => setIsUserDetailsOpen(false)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-md mx-2 md:mx-auto">
          <DialogHeader>
            <DialogTitle>Slet Bruger</DialogTitle>
          </DialogHeader>
          {userToDelete && (
            <div className="space-y-4">
              <p>Er du sikker på at du vil slette denne bruger?</p>
              
              <div className="bg-slate-50 p-4 rounded-lg border">
                <p className="font-semibold">{userToDelete.name || 'Ukendt navn'}</p>
                <p className="text-sm text-slate-600">{userToDelete.email}</p>
              </div>
              
              <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                <p className="text-sm text-red-800">
                  <strong>Advarsel:</strong> Denne handling kan ikke fortrydes. Brugeren vil blive permanent fjernet fra systemet.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setIsDeleteDialogOpen(false)}
                  className="flex-1"
                  disabled={loading}
                >
                  Annuller
                </Button>
                <Button 
                  variant="destructive"
                  onClick={confirmDeleteUser}
                  className="flex-1"
                  disabled={loading}
                >
                  {loading ? 'Sletter...' : 'Slet Bruger'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};